import React, { useState, useEffect, useCallback, useRef } from "react";
import DataGrid, {
  Column,
  Paging,
  Pager,
  FilterRow,
  HeaderFilter,
  SearchPanel,
} from "devextreme-react/data-grid";
import Button from "devextreme-react/button";
import notify from "devextreme/ui/notify";
import axios, { AxiosError } from "axios";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";
import { exportDataGrid } from "devextreme/excel_exporter";
import { useNavigate } from "react-router-dom";
import {
  PageContainer,
  HeaderContainer,
  Title,
  FilterBar,
  Field,
  Label,
  StyledSelect,
  StyledInput,
  DateInput,
  AddUserButton,
  GridContainer,
  DevExtremeModalGlobalStyles,
} from "./TablaActivos.styles";
import {
  Envase,
  HistorialEtiqueta,
  SolicitudDetalle,
  SolicitudCambiosEnvaseDto,
} from "../../dtos/Envase";
import { apiService } from "../../services/apiService";
import AccionPopup from "./AccionPopup";
import DetallePopup from "./DetallePopup";
import { ApiError } from "../../services/apiError";
import {
  envasesService,
  CatalogosEnvase,
} from "../../services/envasesService";
import { productosService } from "../../services/productosService";
import { Productos } from "../../dtos/Productos";
import ProductoAutocomplete from "./ProductoAutocomplete";

const p2 = (n: number) => String(n).padStart(2, "0");
const toYMD = (d: Date) =>
  `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
const todayYMD = () => toYMD(new Date());
const subDaysYMD = (days: number) =>
  toYMD(new Date(Date.now() - days * 24 * 60 * 60 * 1000));

const handleApiError = (err: unknown): ApiError => {
  if (axios.isAxiosError(err)) {
    const error = err as AxiosError<ApiError>;
    return {
      message:
        error.response?.data?.message || "Ocurrió un error en el servidor.",
      status: error.response?.status,
      details: error.response?.data?.details || null,
    };
  }
  return { message: "Ocurrió un error inesperado. Revisa tu conexión de red." };
};

type EditarForm = {
  numeroSerie: string;
  pesoTara: string;
  fabricanteId: string;
  modeloId: string;
  idTipoEnvase: string;
  motivo: string;
};

const emptyEditForm = (): EditarForm => ({
  numeroSerie: "",
  pesoTara: "",
  fabricanteId: "",
  modeloId: "",
  idTipoEnvase: "",
  motivo: "",
});

const PopupOverlay: React.FC<{
  children: React.ReactNode;
  onClose: () => void;
  title: string;
  loading?: boolean;
}> = ({ children, onClose, title, loading }) => (
  <div
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.35)",
      zIndex: 9999,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 16,
    }}
    onClick={onClose}
  >
    <div
      style={{
        width: "100%",
        maxWidth: 900,
        maxHeight: "90vh",
        overflow: "auto",
        background: "#fff",
        borderRadius: 10,
        border: "1px solid #DFE1E6",
        boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        style={{
          padding: "14px 18px",
          borderBottom: "1px solid #EFF2F7",
          fontWeight: 700,
          fontSize: 16,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <span>{title}</span>
        <button type="button" onClick={onClose} style={{ border: "none", background: "transparent" }}>
          ✕
        </button>
      </div>
      <div style={{ padding: 18, opacity: loading ? 0.6 : 1 }}>{children}</div>
    </div>
  </div>
);

const TablaActivos: React.FC = () => {
  const navigate = useNavigate();
  const dataGridRef = useRef<any>(null);
  const [activos, setActivos] = useState<Envase[]>([]);
  const [loading, setLoading] = useState(true);
  const [catalogos, setCatalogos] = useState<CatalogosEnvase | null>(null);

  const [accionPopupVisible, setAccionPopupVisible] = useState(false);
  const [popupLoading, setPopupLoading] = useState(false);
  const [solicitudDetalle, setSolicitudDetalle] = useState<SolicitudDetalle | null>(null);
  const [popupAction, setPopupAction] = useState<"baja" | "edicion" | null>(null);
  const [detallePopupVisible, setDetallePopupVisible] = useState(false);
  const [selectedActivo, setSelectedActivo] = useState<Envase | null>(null);

  const [editPopupVisible, setEditPopupVisible] = useState(false);
  const [bajaPopupVisible, setBajaPopupVisible] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [bajaLoading, setBajaLoading] = useState(false);
  const [editForm, setEditForm] = useState<EditarForm>(emptyEditForm());
  const [editCodigoProducto, setEditCodigoProducto] = useState<number | null>(null);
  const [editProducto, setEditProducto] = useState<Productos | null>(null);
  const [loadingEditProducto, setLoadingEditProducto] = useState(false);
  const [bajaMotivo, setBajaMotivo] = useState("");

  const [filterCodigo, setFilterCodigo] = useState("");
  const [filterSerie, setFilterSerie] = useState("");
  const [filterFabricante, setFilterFabricante] = useState("");
  const [filterModelo, setFilterModelo] = useState("");
  const [filterFrom, setFilterFrom] = useState(subDaysYMD(7));
  const [filterTo, setFilterTo] = useState(todayYMD());

  useEffect(() => {
    envasesService
      .loadCatalogosEnvase()
      .then(setCatalogos)
      .catch((err) => notify(handleApiError(err).message, "error", 3000));
  }, []);

  useEffect(() => {
    apiService
      .getSecondary<HistorialEtiqueta[]>("/api/Envases/HistorialEtiquetas")
      .catch(() => undefined);
  }, []);

  const fetchActivos = useCallback(async () => {
    setLoading(true);
    try {
      const list = await envasesService.getEnvasesFiltered({
        codigo: filterCodigo.trim() || undefined,
        nserie: filterSerie.trim() || undefined,
        fabricanteId: filterFabricante || undefined,
        modeloId: filterModelo || undefined,
        from: filterFrom || undefined,
        to: filterTo || undefined,
      });
      setActivos(Array.isArray(list) ? list : []);
      if (!list?.length) {
        notify({ message: "Sin resultados.", type: "info", displayTime: 1800 });
      }
    } catch (err) {
      setActivos([]);
      notify(handleApiError(err).message, "error", 3000);
    } finally {
      setLoading(false);
    }
  }, [filterCodigo, filterSerie, filterFabricante, filterModelo, filterFrom, filterTo]);

  useEffect(() => {
    fetchActivos();
  }, []);

  const modelosFiltrados =
    catalogos?.modelos.filter(
      (m) => !editForm.fabricanteId || m.fabricanteId === editForm.fabricanteId,
    ) ?? [];

  const openEditPopup = async (data: Envase) => {
    setSelectedActivo(data);
    const codigo = data.codigoProducto ?? null;
    setEditCodigoProducto(codigo);
    setEditProducto(null);
    setEditForm({
      numeroSerie: String(data.numeroSerie ?? ""),
      pesoTara: data.pesoTara != null ? String(data.pesoTara) : "",
      fabricanteId: String(data.fabricanteId ?? ""),
      modeloId: String(data.modeloId ?? ""),
      idTipoEnvase: String(data.idTipoEnvase ?? ""),
      motivo: "",
    });
    setEditPopupVisible(true);

    if (codigo) {
      setLoadingEditProducto(true);
      try {
        const prod = await productosService.getByCodigo(codigo);
        setEditProducto(prod ?? null);
      } catch (err) {
        notify(handleApiError(err).message, "warning", 2500);
      } finally {
        setLoadingEditProducto(false);
      }
    }
  };

  const handleEditProductoChange = async (codigo: number | null) => {
    setEditCodigoProducto(codigo);
    if (!codigo) {
      setEditProducto(null);
      return;
    }
    setLoadingEditProducto(true);
    try {
      const prod = await productosService.getByCodigo(codigo);
      setEditProducto(prod ?? null);
    } catch (err) {
      setEditProducto(null);
      notify(handleApiError(err).message, "error", 3000);
    } finally {
      setLoadingEditProducto(false);
    }
  };

  const editProductoCompleto =
    !!editProducto &&
    !!editProducto.codigoCuerpo &&
    !!editProducto.codigoCapuchon &&
    !!editProducto.codigoCuello &&
    !!editProducto.codigoEstarcido &&
    !!editProducto.codigoOjiva &&
    editProducto.idValvula != null;

  const catalogoReadonlyItem = (
    label: string,
    codigo?: string | null,
    extra?: string | null,
  ) => (
    <div>
      <Label>{label}</Label>
      <StyledInput
        value={codigo ? (extra ? `${codigo} — ${extra}` : codigo) : "—"}
        readOnly
        disabled
      />
    </div>
  );

  const submitEdit = async () => {
    if (!editForm.motivo.trim()) {
      notify("Indica un motivo para la edición.", "warning", 2000);
      return;
    }
    if (!editForm.fabricanteId || !editForm.modeloId || !editForm.idTipoEnvase) {
      notify(
        "Fabricante, modelo y tipo de envase son obligatorios.",
        "warning",
        2500,
      );
      return;
    }
    if (editCodigoProducto && !editProductoCompleto) {
      notify(
        "El producto seleccionado no tiene la configuración de catálogos completa.",
        "warning",
        3000,
      );
      return;
    }

    const catalogosSnapshot = editProducto
      ? {
          codigoCuerpo: editProducto.codigoCuerpo ?? null,
          codigoCapuchon: editProducto.codigoCapuchon ?? null,
          codigoCuello: editProducto.codigoCuello ?? null,
          codigoEstarcido: editProducto.codigoEstarcido ?? null,
          codigoOjiva: editProducto.codigoOjiva ?? null,
          idValvula: editProducto.idValvula ?? null,
        }
      : {
          codigoCuerpo: selectedActivo?.codigoCuerpo ?? null,
          codigoCapuchon: selectedActivo?.codigoCapuchon ?? null,
          codigoCuello: selectedActivo?.codigoCuello ?? null,
          codigoEstarcido: selectedActivo?.codigoEstarcido ?? null,
          codigoOjiva: selectedActivo?.codigoOjiva ?? null,
          idValvula: selectedActivo?.idValvula ?? null,
        };

    setEditLoading(true);
    try {
      const body: SolicitudCambiosEnvaseDto = {
        numeroSerie: editForm.numeroSerie,
        pesoTara: editForm.pesoTara.trim() === "" ? null : Number(editForm.pesoTara),
        codigoProducto: editCodigoProducto,
        fabricanteId: editForm.fabricanteId || null,
        modeloId: editForm.modeloId || null,
        idTipoEnvase:
          editForm.idTipoEnvase.trim() === ""
            ? null
            : Number(editForm.idTipoEnvase),
        ...catalogosSnapshot,
        motivo: editForm.motivo,
      };
      await apiService.post("/api/Envases/SolicitaCambio", body);
      notify("Solicitud de cambio enviada.", "success", 2200);
      setEditPopupVisible(false);
      await fetchActivos();
    } catch (err) {
      notify(handleApiError(err).message, "error", 3000);
    } finally {
      setEditLoading(false);
    }
  };

  const submitBaja = async () => {
    if (!selectedActivo?.numeroSerie || !bajaMotivo.trim()) {
      notify("Indica un motivo para la baja.", "warning", 2000);
      return;
    }
    setBajaLoading(true);
    try {
      await apiService.post("/api/Envases/SolicitaBaja", {
        numeroSerie: selectedActivo.numeroSerie,
        motivo: bajaMotivo,
      });
      notify("Solicitud de baja enviada.", "success", 2000);
      setBajaPopupVisible(false);
      await fetchActivos();
    } catch (err) {
      notify(handleApiError(err).message, "error", 3000);
    } finally {
      setBajaLoading(false);
    }
  };

  const handleOpenAccionPopup = async (data: Envase, action: "baja" | "edicion") => {
    setSelectedActivo(data);
    setPopupAction(action);
    setAccionPopupVisible(true);
    setPopupLoading(true);
    try {
      const detalles = await apiService.getSecondary<SolicitudDetalle[]>(
        `/api/Envases/solicitud/NumSerie/${data.numeroSerie}`,
      );
      if (detalles?.[0]) setSolicitudDetalle(detalles[0]);
      else throw new Error("Sin detalles de solicitud.");
    } catch (err) {
      notify(handleApiError(err).message, "error", 3000);
      setAccionPopupVisible(false);
    } finally {
      setPopupLoading(false);
    }
  };

  const handleConfirmAction = async () => {
    if (!selectedActivo) return;
    const payloadBase = {
      numeroserie: selectedActivo.numeroSerie,
      aceptar: true,
      motivo: solicitudDetalle?.motivo || "Aprobado desde sistema",
      codigoProducto: solicitudDetalle?.codigoProducto?.toString() || null,
    };
    try {
      await apiService.post("/api/Envases/ConfirmaCabio", {
        ...payloadBase,
        baja: popupAction === "baja",
      });
      notify("Solicitud confirmada.", "success", 2000);
      setAccionPopupVisible(false);
      fetchActivos();
    } catch (err) {
      notify(handleApiError(err).message, "error", 3000);
    }
  };

  const getEstatus = (row: Envase) =>
    typeof row.statusActivo === "string" ? row.statusActivo : "";

  const handleExportExcel = async () => {
    const gridInstance = dataGridRef.current?.instance();
    if (!gridInstance) {
      notify("No se pudo exportar.", "error", 3000);
      return;
    }
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Envases");
    await exportDataGrid({ component: gridInstance, worksheet, autoFilterEnabled: true });
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: "application/octet-stream" }), "Envases.xlsx");
  };

  const renderCatalogSelect = (
    label: string,
    field: "fabricanteId" | "modeloId" | "idTipoEnvase",
    options: { value: string; label: string }[],
    onChangeExtra?: (value: string) => Partial<EditarForm>,
  ) => (
    <div key={field}>
      <Label>{label}</Label>
      <StyledSelect
        value={editForm[field]}
        onChange={(e) => {
          const value = e.target.value;
          setEditForm((f) => ({
            ...f,
            [field]: value,
            ...(onChangeExtra ? onChangeExtra(value) : {}),
          }));
        }}
      >
        <option value="">Selecciona...</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </StyledSelect>
    </div>
  );

  return (
    <>
      <DevExtremeModalGlobalStyles />
      <PageContainer>
        <div style={{ display: "flex", gap: 12, marginBottom: 15, flexWrap: "wrap" }}>
          <Button icon="chevronleft" text="Regresar" stylingMode="outlined" onClick={() => navigate(-1)} />
          <Button
            icon="add"
            text="Registrar envase"
            type="default"
            stylingMode="contained"
            onClick={() => navigate("/consulta-activos/registrar")}
          />
          <Button
            icon="edit"
            text="Completar sin producto"
            stylingMode="outlined"
            onClick={() => navigate("/consulta-activos/completar")}
          />
        </div>
        <HeaderContainer>
          <Title>Listado de Envases</Title>
        </HeaderContainer>

        <FilterBar>
          <Field>
            <Label>Código producto</Label>
            <StyledInput
              value={filterCodigo}
              onChange={(e) => setFilterCodigo(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchActivos()}
            />
          </Field>
          <Field>
            <Label>Número de serie</Label>
            <StyledInput
              value={filterSerie}
              onChange={(e) => setFilterSerie(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchActivos()}
            />
          </Field>
          <Field>
            <Label>Fabricante</Label>
            <StyledSelect value={filterFabricante} onChange={(e) => setFilterFabricante(e.target.value)}>
              <option value="">Todos</option>
              {catalogos?.fabricantes.map((f) => (
                <option key={f.id} value={f.id}>{f.nombre}</option>
              ))}
            </StyledSelect>
          </Field>
          <Field>
            <Label>Modelo</Label>
            <StyledSelect value={filterModelo} onChange={(e) => setFilterModelo(e.target.value)}>
              <option value="">Todos</option>
              {catalogos?.modelos.map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </StyledSelect>
          </Field>
          <Field>
            <Label>Fecha inicio</Label>
            <DateInput type="date" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} />
          </Field>
          <Field>
            <Label>Fecha fin</Label>
            <DateInput type="date" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} />
          </Field>
          <AddUserButton onClick={fetchActivos}>Buscar</AddUserButton>
        </FilterBar>

        <GridContainer>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
            <Button icon="xlsxfile" text="Exportar Excel" onClick={handleExportExcel} />
          </div>
          <DataGrid
            ref={dataGridRef}
            dataSource={activos}
            keyExpr="id"
            showBorders
            columnAutoWidth
            wordWrapEnabled
            noDataText={loading ? "Cargando..." : "Sin resultados"}
            loadPanel={{ enabled: loading }}
          >
            <Column dataField="numeroSerie" caption="Serie" />
            <Column dataField="codigoProducto" caption="Cód. producto" width={110} />
            <Column caption="Producto" calculateCellValue={(r: Envase) => r.descripcionProducto ?? ""} />
            <Column dataField="fabricante" caption="Fabricante" />
            <Column dataField="modelo" caption="Modelo" />
            <Column dataField="tipoDeEnvase" caption="Tipo" />
            <Column dataField="codigoCuerpo" caption="Cuerpo" />
            <Column dataField="codigoCapuchon" caption="Capuchón" />
            <Column dataField="codigoCuello" caption="Cuello" />
            <Column dataField="valvula" caption="Válvula" />
            <Column caption="Estatus" calculateCellValue={getEstatus} />
            <Column
              caption="Acciones"
              width={300}
              allowExporting={false}
              cellRender={({ data }: { data: Envase }) => (
                <div style={{ display: "flex", gap: 6 }}>
                  <Button icon="info" hint="Detalle" onClick={() => { setSelectedActivo(data); setDetallePopupVisible(true); }} />
                  <Button icon="edit" type="success" hint="Revisar edición" disabled={getEstatus(data) !== "SolicitudEdicion"} onClick={() => handleOpenAccionPopup(data, "edicion")} />
                  <Button icon="trash" type="danger" hint="Revisar baja" disabled={getEstatus(data) !== "SolicitudBaja"} onClick={() => handleOpenAccionPopup(data, "baja")} />
                  <Button icon="edit" hint="Solicitar cambio" onClick={() => openEditPopup(data)} />
                  <Button icon="trash" hint="Solicitar baja" onClick={() => { setSelectedActivo(data); setBajaMotivo(""); setBajaPopupVisible(true); }} />
                </div>
              )}
            />
            <FilterRow visible />
            <HeaderFilter visible />
            <SearchPanel visible width={240} />
            <Paging defaultPageSize={50} />
            <Pager showInfo />
          </DataGrid>
        </GridContainer>
      </PageContainer>

      <AccionPopup
        visible={accionPopupVisible}
        loading={popupLoading}
        onClose={() => setAccionPopupVisible(false)}
        onConfirm={handleConfirmAction}
        detalle={solicitudDetalle}
        action={popupAction}
      />
      <DetallePopup
        visible={detallePopupVisible}
        onClose={() => setDetallePopupVisible(false)}
        activo={selectedActivo}
      />

      {editPopupVisible && catalogos && (
        <PopupOverlay
          title={`Solicitar cambio — ${editForm.numeroSerie}`}
          onClose={() => setEditPopupVisible(false)}
          loading={editLoading}
        >
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <Label>Número de serie</Label>
              <StyledInput value={editForm.numeroSerie} disabled />
            </div>
            <div>
              <Label>Código producto</Label>
              <ProductoAutocomplete
                value={editCodigoProducto}
                onChange={handleEditProductoChange}
              />
            </div>
            <div>
              <Label>Peso tara</Label>
              <StyledInput value={editForm.pesoTara} onChange={(e) => setEditForm((f) => ({ ...f, pesoTara: e.target.value }))} />
            </div>
            {renderCatalogSelect(
              "Fabricante *",
              "fabricanteId",
              catalogos.fabricantes.map((f) => ({ value: f.id, label: f.nombre ?? "" })),
              () => ({ modeloId: "" }),
            )}
            {renderCatalogSelect(
              "Modelo *",
              "modeloId",
              modelosFiltrados.map((m) => ({ value: m.id, label: m.name ?? "" })),
            )}
            {renderCatalogSelect(
              "Tipo envase *",
              "idTipoEnvase",
              catalogos.tiposEnvase.map((t) => ({
                value: String(t.idTipoEnvase),
                label: t.tipoDeEnvase || t.descripcion,
              })),
            )}

            <div style={{ gridColumn: "1 / -1" }}>
              <h4 style={{ margin: "8px 0", color: "#42526E" }}>
                Catálogos del producto (solo lectura)
              </h4>
              {!editCodigoProducto && (
                <p style={{ fontSize: 13, color: "#b45309", margin: "0 0 8px" }}>
                  Sin producto: se conservarán los catálogos actuales del envase en la solicitud.
                </p>
              )}
              {!!editCodigoProducto && loadingEditProducto && (
                <p style={{ fontSize: 13, color: "#6B778C" }}>Cargando producto...</p>
              )}
              {!!editCodigoProducto && !loadingEditProducto && editProducto && !editProductoCompleto && (
                <p style={{ fontSize: 13, color: "#dc2626", margin: "0 0 8px" }}>
                  El producto no tiene la configuración de catálogos completa.
                </p>
              )}
            </div>

            {editProducto ? (
              <>
                {catalogoReadonlyItem("Cuerpo", editProducto.codigoCuerpo, editProducto.colorCuerpo)}
                {catalogoReadonlyItem("Capuchón", editProducto.codigoCapuchon, editProducto.colorCapuchon)}
                {catalogoReadonlyItem("Cuello", editProducto.codigoCuello, editProducto.colorCuello)}
                {catalogoReadonlyItem("Estarcido", editProducto.codigoEstarcido, editProducto.colorEstarcido)}
                {catalogoReadonlyItem("Ojiva", editProducto.codigoOjiva, editProducto.colorOjiva)}
                {catalogoReadonlyItem(
                  "Válvula",
                  editProducto.idValvula != null ? String(editProducto.idValvula) : null,
                  editProducto.valvula,
                )}
              </>
            ) : (
              selectedActivo && (
                <>
                  {catalogoReadonlyItem("Cuerpo", selectedActivo.codigoCuerpo)}
                  {catalogoReadonlyItem("Capuchón", selectedActivo.codigoCapuchon)}
                  {catalogoReadonlyItem("Cuello", selectedActivo.codigoCuello)}
                  {catalogoReadonlyItem("Estarcido", selectedActivo.codigoEstarcido)}
                  {catalogoReadonlyItem("Ojiva", selectedActivo.codigoOjiva)}
                  {catalogoReadonlyItem(
                    "Válvula",
                    selectedActivo.idValvula != null ? String(selectedActivo.idValvula) : null,
                    selectedActivo.valvula,
                  )}
                </>
              )
            )}

            <div style={{ gridColumn: "1 / -1" }}>
              <Label>Motivo *</Label>
              <StyledInput value={editForm.motivo} onChange={(e) => setEditForm((f) => ({ ...f, motivo: e.target.value }))} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
            <AddUserButton onClick={() => setEditPopupVisible(false)}>Cancelar</AddUserButton>
            <AddUserButton onClick={submitEdit} disabled={editLoading}>
              {editLoading ? "Enviando..." : "Enviar solicitud"}
            </AddUserButton>
          </div>
        </PopupOverlay>
      )}

      {bajaPopupVisible && (
        <PopupOverlay
          title={`Baja — ${selectedActivo?.numeroSerie ?? ""}`}
          onClose={() => setBajaPopupVisible(false)}
          loading={bajaLoading}
        >
          <Label>Motivo</Label>
          <StyledInput value={bajaMotivo} onChange={(e) => setBajaMotivo(e.target.value)} />
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
            <AddUserButton onClick={() => setBajaPopupVisible(false)}>Cancelar</AddUserButton>
            <AddUserButton onClick={submitBaja} disabled={bajaLoading}>Confirmar</AddUserButton>
          </div>
        </PopupOverlay>
      )}
    </>
  );
};

export default TablaActivos;
